/**
 * Authentication API Service
 * Handles all authentication-related API calls
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    ApiResponse,
    LoginRequest,
    LoginResponse,
    ForgotPasswordRequest,
    ForgotPasswordResponse,
    ResetPasswordRequest,
    ResetPasswordResponse,
    RefreshTokenRequest,
    RefreshTokenResponse,
} from '@/types/auth'

/**
 * Admin Login
 * POST /auth/admin/login
 */
export const adminLogin = async (
    credentials: LoginRequest
): Promise<ApiResponse<LoginResponse>> => {
    const response = await axiosInstance.post<ApiResponse<LoginResponse>>(
        API_ROUTES.AUTH.ADMIN_LOGIN,
        credentials
    )
    return response.data
}

/**
 * Admin Logout
 * POST /auth/admin/logout
 */
export const adminLogout = async (refreshToken?: string): Promise<void> => {
    try {
        // If refreshToken is provided, send it in the request body
        if (refreshToken) {
            await axiosInstance.post(API_ROUTES.AUTH.ADMIN_LOGOUT, {
                refreshToken,
            })
        } else {
            await axiosInstance.post(API_ROUTES.AUTH.ADMIN_LOGOUT)
        }
    } catch (error) {
        // Ignore logout errors, still clear local state
        console.error('Logout error:', error)
    }
}

/**
 * Forgot Password
 * POST /auth/admin/forgot-password
 */
export const forgotPassword = async (
    data: ForgotPasswordRequest
): Promise<ApiResponse<ForgotPasswordResponse>> => {
    const response = await axiosInstance.post<
        ApiResponse<ForgotPasswordResponse>
    >(API_ROUTES.AUTH.FORGOT_PASSWORD, data)
    return response.data
}

/**
 * Reset Password
 * POST /auth/admin/reset-password
 */
export const resetPassword = async (
    data: ResetPasswordRequest
): Promise<ApiResponse<ResetPasswordResponse>> => {
    const response = await axiosInstance.post<
        ApiResponse<ResetPasswordResponse>
    >(API_ROUTES.AUTH.RESET_PASSWORD, data)
    return response.data
}

/**
 * Refresh Token
 * POST /auth/admin/refresh-token
 */
export const refreshToken = async (
    data: RefreshTokenRequest
): Promise<ApiResponse<RefreshTokenResponse>> => {
    const response = await axiosInstance.post<
        ApiResponse<RefreshTokenResponse>
    >(API_ROUTES.AUTH.REFRESH_TOKEN, data)
    return response.data
}

/**
 * Verify Token
 * POST /auth/admin/verify-token
 */
export const verifyToken = async (
    token: string
): Promise<ApiResponse<{ valid: boolean }>> => {
    const response = await axiosInstance.post<ApiResponse<{ valid: boolean }>>(
        API_ROUTES.AUTH.VERIFY_TOKEN,
        { token }
    )
    return response.data
}
